// Storyblok image service: https://www.storyblok.com/docs/image-service
// Serving the original asset costs a lot of Storyblok traffic (some uploads are 12000px / 2.5MB),
// so every Storyblok image must go through this helper instead of using `filename` directly.
const STORYBLOK_ASSET_REGEX = /^https:\/\/a\.storyblok\.com\/f\/\d+\/(\d+)x(\d+)\//;
const NOT_RESIZABLE_EXTENSIONS_REGEX = /\.(svg|gif)$/i;
const DEFAULT_QUALITY = 75;

const isResizable = (src) =>
  STORYBLOK_ASSET_REGEX.test(src) && !NOT_RESIZABLE_EXTENSIONS_REGEX.test(src);

const getOriginalWidth = (src) => Number(src.match(STORYBLOK_ASSET_REGEX)?.[1]) || 0;

export const storyblokImage = (src, { width = 0, quality = DEFAULT_QUALITY } = {}) => {
  if (!src || !isResizable(src)) return src;

  // Never upscale: the image service would serve a bigger file than the original.
  const originalWidth = getOriginalWidth(src);
  const resizeWidth = originalWidth && width > originalWidth ? originalWidth : width;

  return `${src}/m/${resizeWidth}x0/filters:quality(${quality}):format(webp)`;
};

export const storyblokSrcSet = (src, widths, options = {}) => {
  if (!src || !isResizable(src)) return undefined;

  const originalWidth = getOriginalWidth(src);
  const usableWidths = widths.filter((width) => !originalWidth || width <= originalWidth);
  // Keep at least one candidate when the original is smaller than every requested width.
  const srcSetWidths = usableWidths.length ? usableWidths : [originalWidth];

  return srcSetWidths
    .map((width) => `${storyblokImage(src, { ...options, width })} ${width}w`)
    .join(", ");
};

// Widths used for the srcset of each kind of Storyblok image.
export const STORYBLOK_IMAGE_WIDTHS = {
  fullWidthDesktop: [1280, 1920, 2560],
  fullWidthMobile: [480, 768, 1080],
  card: [400, 600, 900, 1200],
};
